import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApi } from 'contexts/api-context'

import type { UseMutationOptions, UseMutationResult, UseQueryOptions } from '@tanstack/react-query'
import type { Device, UseQueryOmissions } from 'types'

type GetCustomerEncodersParams = {
  startdate?: string
  enddate?: string
}

const buildGetEncodersUrl = (customerGuid?: string, startDate?: string, endDate?: string) => {
  const params: GetCustomerEncodersParams = {}
  if (startDate) params.startdate = startDate
  if (endDate) params.enddate = endDate

  let url = `/customers/${customerGuid}/encoders`
  if (params) url += `?${new URLSearchParams(params)}`

  return url
}

export const useGetCustomerEncoders = (
  props: GetCustomerEncodersParams & { customerGuid?: string },
  queryProps?: Omit<UseQueryOptions<Device[], Error>, UseQueryOmissions>
) => {
  const { customerGuid, startdate, enddate } = props
  const { enabled, ...rest } = queryProps ?? {}
  const api = useApi()
  return useQuery<Device[], Error>({
    queryKey: ['customerEncoders', { customerGuid, startdate, enddate }],
    queryFn: async () => (await api.get(buildGetEncodersUrl(customerGuid, startdate, enddate))).encoders,
    enabled: !!customerGuid && enabled !== false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...rest,
  })
}

type PostUpdateDeviceProps = {
  device: Device
}

export type PostUpdateDeviceResponse = {
  result: {
    statusCode: number
    value?: Device
  }
}

export const useUpdateDevice = (
  options?: Omit<UseMutationOptions<PostUpdateDeviceResponse, Error, PostUpdateDeviceProps>, 'mutationFn'>
): UseMutationResult<PostUpdateDeviceResponse, Error, PostUpdateDeviceProps> => {
  const queryClient = useQueryClient()
  const api = useApi()
  return useMutation<PostUpdateDeviceResponse, Error, PostUpdateDeviceProps>({
    ...options,
    mutationFn: vars => api.post('/devices', { body: JSON.stringify(vars.device) }),
    onSuccess: () => {
      queryClient.invalidateQueries(['customerEncoders'])
    },
  })
}
